import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import style from './ui-progress-bar.css?inline';
import '../ui-icons/ui-bold-checkmark-icon';

const CName = 'ui-progress-bar';

@customElement(CName)
export class UiProgressBar extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: Number }) progress = 0.2;
  @property({ type: Number }) remainingAmount = 0;
  @property({ type: String }) from = '';
  @property({ type: Number }) stepsNumber = 0;
  @property({ type: String }) bonusPerStep?: string = undefined;
  @property({ type: String }) to = '';
  @property({ type: Boolean }) hasError = false;
  @property({ type: Boolean }) showText = true;
  @property({ type: Boolean }) isDisabled = false;
  @property({ type: Boolean }) isIncentiveStyle = false;
  @property({ type: Boolean }) isIncentiveGlow = false;

  render() {
    const classes = classMap({
      pb: true,
      'pb--fulfilled': this.progress === 100,
      'pb--has-steps': this.stepsNumber > 0,
      'pb--incentive': this.isIncentiveStyle,
      'pb--incentive-glow': this.isIncentiveGlow,
      'pb--has-error': this.hasError,
      'pb--disabled': this.isDisabled,
    });
    return html`
      <style>
        :host {
          --progress-translate: ${this.progress}%;
        }
      </style>
      <div class=${classes}>
        <div class="pb__bar">
          <div class="pb__line"></div>
          ${this.stepsNumber ? this._renderSteps() : nothing}
        </div>
        ${this.remainingAmount ? this._renderRemainingAmount() : nothing}
        ${this.showText ? this._renderProgressText(this.progress) : nothing}
      </div>
    `;
  }

  private _renderRemainingAmount() {
    return html`
      <div class="pb__remaining-amount">
        ${window.$app.$t.get(I18n.base.exactProgress, { amount: this.remainingAmount.toString() })}
      </div>
    `;
  }

  private _renderProgressText(roundedProgress: number) {
    if (this.from.length && this.to.length) {
      return html`<div class="pb__percentage pb__percentage--term">
        <span>${this.from.replace(' ', '. ')}</span><span>${this.to.replace(' ', '. ')}</span>
      </div>`;
    }

    return html`<div class="pb__percentage">${roundedProgress}%</div>`;
  }

  private _renderSteps() {
    const activeBulletAmount = Math.floor((this.progress / 100) * this.stepsNumber);

    const steps = [];
    for (let i = 0; i < this.stepsNumber; i++) {
      steps.push(
        html`<div class="pb__step ${activeBulletAmount > i ? 'pb__step--active' : ''}">
          <div class="pb__step-bullet">
            <ui-bold-checkmark-icon class="pb__step-checkmark"></ui-bold-checkmark-icon>
          </div>
          ${this.bonusPerStep ? html`<div class="pb__step-text">${this.bonusPerStep}</div>` : nothing}
        </div>`,
      );
    }
    return html`<div class="pb__steps">${steps}</div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiProgressBar;
  }
}
